import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supabaseService from '../services/supabaseService';
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';

const VerifyRecoveryScreen = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // 1. محاولة جلب الايميل من الرابط (الأسلوب الأفضل)
    const emailFromUrl = searchParams.get('email');
    
    if (emailFromUrl) {
      setEmail(emailFromUrl);
      // حفظه محلياً لاستخدامه لاحقاً
      localStorage.setItem('recovery_email', emailFromUrl);
      return;
    }

    // 2. إذا لم يكن في الرابط، نحاول من التخزين المحلي (لمنع فقدان البيانات عند التحديث)
    const storedEmail = localStorage.getItem('recovery_email');
    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      // إذا لم نجد أي ايميل، نعيد المستخدم
      toast.error('لم يتم العثور على بريد إلكتروني، يرجى البدء من جديد');
      navigate('/forgot-password');
    }
  }, [searchParams, navigate]);

  const handleResend = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await supabaseService.client.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      toast.success('تم إرسال رمز جديد إلى بريدك الإلكتروني');
    } catch (error) {
      toast.error('فشل إعادة إرسال الرمز');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code || code.length < 8) {
      return toast.error('يرجى إدخال الرمز المكون من 8 أرقام');
    }

    if (!email) {
      return toast.error('خطأ: البريد الإلكتروني مفقود');
    }

    setLoading(true);
    try {
      // التحقق من الرمز
      const { error } = await supabaseService.verifyOtp(email, code, 'recovery');

      if (error) throw error;

      toast.success('تم التحقق من الرمز بنجاح');
      
      // الانتقال لإعادة تعيين كلمة المرور
      navigate('/reset-password');
      
    } catch (error) {
      console.error('Verification Error:', error);
      toast.error(error.message || 'الرمز غير صحيح أو منتهي الصلاحية');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* زر العودة */}
        <button
          onClick={() => {
            localStorage.removeItem('recovery_email');
            navigate('/forgot-password');
          }}
          className="mb-6 flex items-center text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium"
        >
          <FiArrowLeft className="ml-2" />
          العودة
        </button>

        {/* العنوان والوصف */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">تأكيد الاستعادة</h2>
          <p className="text-gray-500 mt-2 text-sm">
            أدخل الرمز المكون من 8 أرقام المرسل إلى بريدك الإلكتروني
          </p>
          <p className="text-gray-400 text-xs mt-2 break-all font-medium">
            {email}
          </p>
        </div>
        
        {/* النموذج */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center">
            <div className="relative w-full">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="00000000"
                className="w-full text-center text-2xl font-bold tracking-[0.2em] py-4 border-b-2 border-gray-200 focus:border-gray-900 focus:outline-none bg-transparent text-gray-900 placeholder-gray-200 transition-colors"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
                maxLength={8}
                required
                autoFocus
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading || code.length < 8}
            className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                جاري التحقق...
              </div>
            ) : (
              'تحقق ومتابعة'
            )}
          </button>
        </form>

        {/* إعادة الإرسال */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm mb-2">لم يصلك الرمز؟</p>
          <button
            onClick={handleResend}
            disabled={loading}
            className="text-gray-900 font-bold hover:underline disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
          >
            <FiRefreshCw className="w-4 h-4" />
            إعادة الإرسال
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyRecoveryScreen;